// v0.5.0f — Walkthrough B (systemic) decision UI.
//
// Ordered options:
//   ✓ RECOMMENDED — FIX UPSTREAM (defer this country's filing).
//   ⚠ FILE TODAY — Bulk-route all N txns to manual box (DE/FR).
//
// Bulk-drop is intentionally NOT offered (not disabled — removed).
import React from "react";
import { useTranslation } from "react-i18next";

const fmtPct = (rate) => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return rate;
  return `${(n * 100).toFixed(n * 100 % 1 === 0 ? 0 : 2)}%`;
};

const fmtMoney = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

const adapterTitle = (a) => {
  const k = (a || "").toLowerCase();
  return ({
    client_template: "Client template",
  })[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1) : "unknown");
};

export const InspectDecisionB = ({
  offender, country, aggregate, txns,
  supportsRoute, manualBoxId,
  pickDecision, flagged, flagBusy,
}) => {
  const { t } = useTranslation();
  const cc = (country || "").toUpperCase();
  const txnCount = (txns || []).length;
  const totalVat = (txns || []).reduce(
    (s, t) => s + Number(t.vat_amount || 0), 0,
  );
  return (
    <div
      data-testid={`inspect-decision-b-${offender.rate}`}
      className="space-y-3 mt-4"
    >
      <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 p-4">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300 tabular-nums">
            {t("ui.country_return.inspect_b.recommended", { defaultValue: "✓ Recommended" })}
          </span>
        </div>
        <div className="font-bold text-sm text-emerald-900 dark:text-emerald-100">
          {t("ui.country_return.inspect_b.fix_at_source", { cc, defaultValue: `Fix at source — defer ${cc} filing for this period` })}
        </div>
        <div className="text-xs text-emerald-900/80 dark:text-emerald-100/80 leading-snug mt-1">
          {t("ui.country_return.inspect_b.fix_body", { adapter: adapterTitle(aggregate?.adapter_dominant), cc, rate: fmtPct(offender.rate), defaultValue: `Check ${adapterTitle(aggregate?.adapter_dominant)}'s ${cc} tax setting. ${fmtPct(offender.rate)} almost certainly came from a misconfigured rate. Re-upload the corrected data when fixed.` })}
        </div>
        <button
          type="button"
          data-testid={`inspect-fix-upstream-btn-${offender.rate}`}
          data-resolution-class="recordkeeping"
          data-resolution-endpoint="POST /api/calc/{calc_id}/rate-table-drift-flags"
          onClick={() => pickDecision("fix_upstream")}
          disabled={flagged || flagBusy}
          className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
        >
          {flagged ? t("ui.country_return.inspect_b.reported", { defaultValue: "✓ Reported · defers filing" }) : t("ui.country_return.inspect_b.report", { defaultValue: "Report to maintainer → defer filing" })}
        </button>
      </div>
      {supportsRoute && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-4">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300 tabular-nums">
              {t("ui.country_return.inspect_b.file_today", { defaultValue: "⚠ File today — use with caution" })}
            </span>
          </div>
          <div className="font-bold text-sm text-amber-900 dark:text-amber-100">
            {t("ui.country_return.inspect_b.assign_all", { n: txnCount, cc, box: manualBoxId, defaultValue: `Assign all ${txnCount} → ${cc} manual-other-rates box (Kz ${manualBoxId})` })}
          </div>
          <div className="text-xs text-amber-900/80 dark:text-amber-100/80 leading-snug mt-1">
            {t("ui.country_return.inspect_b.only_if", { rate: fmtPct(offender.rate), cc, defaultValue: `Only if you've confirmed upstream is correct and ${fmtPct(offender.rate)} really is a new ${cc} rate that just isn't in our table yet.` })}
          </div>
          <button
            type="button"
            data-testid={`inspect-bulk-route-btn-${offender.rate}`}
            data-resolution-class="self-resolve"
            data-resolution-endpoint="POST /api/runs/calc/{calc_id}/txn-override"
            onClick={() => pickDecision("bulk_route")}
            className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white"
          >
            {t("ui.country_return.inspect_b.confirm_assign", { n: txnCount, defaultValue: `Confirm — assign all ${txnCount} →` })}
          </button>
        </div>
      )}
      <div
        data-testid={`inspect-bulk-drop-absent-${offender.rate}`}
        className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug px-1"
      >
        <span className="text-rose-600 dark:text-rose-400 font-bold">✗</span>
        {" "}
        {t("ui.country_return.inspect_b.no_bulk_drop", { n: txnCount, vat: fmtMoney(totalVat), defaultValue: `Bulk-dropping is not offered when the pattern looks systemic. ${txnCount} transactions worth €${fmtMoney(totalVat)} of VAT cannot be silently dropped from a filing.` })}
      </div>
    </div>
  );
};

export default InspectDecisionB;
